import { IdToken } from '@auth0/auth0-spa-js';
import { Auth0UserInfo } from '../interfaces/account';
import { withExponentialBackoffSync } from '../utils/exponentialBackoff';

const AUTH_DOMAIN = process.env.REACT_APP_AUTH0_DOMAIN;

/**
 * Enhanced secure token service with memory-only storage
 * and additional security measures
 *
 * Features:
 * - In-memory token storage for security
 * - Token expiry validation
 * - Automatic token clearing on page unload
 * - Inactivity timeout monitoring (30 minutes)
 * - Retry logic for temporary token unavailability during team switches
 */
class EnhancedSecureTokenService {
  private static instance: EnhancedSecureTokenService;
  private accessToken: string | null = null;
  private idToken: IdToken | null = null;
  private refreshTimer: NodeJS.Timeout | null = null;
  private tokenExpiryTime: number | null = null;

  // Security timeouts
  private readonly MAX_TOKEN_AGE = 60 * 60 * 1000; // 1 hour max
  private readonly REFRESH_BUFFER = 5 * 60 * 1000; // 5 minutes before expiry
  private readonly INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes

  private constructor() {
    // Generate a unique session key for this instance
    // this.sessionKey = this.generateSessionKey();

    // Set up activity monitoring
    this.setupActivityMonitoring();

    // Clear tokens on page unload/hide
    this.setupUnloadHandlers();
  }

  static getInstance(): EnhancedSecureTokenService {
    if (!EnhancedSecureTokenService.instance) {
      EnhancedSecureTokenService.instance = new EnhancedSecureTokenService();
    }
    return EnhancedSecureTokenService.instance;
  }

  // For future use with proper encryption
  // private generateSessionKey(): string {
  //   const array = new Uint8Array(32);
  //   crypto.getRandomValues(array);
  //   return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join(
  //     ''
  //   );
  // }

  private setupActivityMonitoring(): void {
    let lastActivity = Date.now();
    const inactivityTimeout = this.INACTIVITY_TIMEOUT; // 30 minutes

    const updateActivity = () => {
      lastActivity = Date.now();
    };

    // Monitor user activity
    ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach((event) => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    // Check for inactivity periodically
    setInterval(() => {
      if (Date.now() - lastActivity > inactivityTimeout) {
        this.clearTokens('inactivity');
      }
    }, 60000); // Check every minute
  }

  private setupUnloadHandlers(): void {
    // Clear tokens on page unload for security
    // Note: We don't clear tokens on visibility changes (tab switching) because
    // Auth0 manages its own token cache and lifecycle. Clearing tokens on visibility
    // changes caused race conditions where components made API calls before
    // AuthenticationGuard could restore tokens from Auth0, resulting in
    // unauthenticated requests and 429 rate limiting errors.
    window.addEventListener('beforeunload', () => {
      this.clearTokens('unload');
    });
  }

  setTokens(accessToken: string, idToken: IdToken): void {
    // Validate tokens before storing
    if (!this.validateToken(accessToken) || !this.validateIdToken(idToken)) {
      console.error('Invalid tokens provided');
      return;
    }

    this.accessToken = accessToken;
    this.idToken = idToken;

    // Calculate expiry time
    const expiresIn = ((idToken.exp || 0) - (idToken.iat || 0)) * 1000;
    this.tokenExpiryTime = Date.now() + Math.min(expiresIn, this.MAX_TOKEN_AGE);

    // Set up refresh timer
    this.setupRefreshTimer();

    // Do not log sensitive token data
    console.debug('Tokens updated successfully');
  }

  private validateToken(token: string): boolean {
    // Basic token validation
    return (
      typeof token === 'string' && token.length > 0 && !token.includes(' ')
    );
  }

  private validateIdToken(idToken: IdToken): boolean {
    // Validate ID token structure and expiry
    // We validate expiry to prevent storing expired tokens
    // Auth0 manages token lifecycle, but we should reject expired tokens
    const now = Date.now() / 1000; // Convert to seconds for comparison with exp
    return (
      idToken &&
      typeof idToken.sub === 'string' &&
      typeof idToken.iat === 'number' &&
      typeof idToken.exp === 'number' &&
      idToken.exp > now // Token must not be expired
    );
  }

  private setupRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    if (!this.tokenExpiryTime) return;

    const timeUntilRefresh =
      this.tokenExpiryTime - Date.now() - this.REFRESH_BUFFER;

    if (timeUntilRefresh > 0) {
      this.refreshTimer = setTimeout(() => {
        this.refreshTokens();
      }, timeUntilRefresh);
    }
  }

  private async refreshTokens(): Promise<void> {
    try {
      // Token refresh logic would go here
      // For now, clear tokens and force re-authentication
      console.debug('Token refresh required');
      this.clearTokens('expired');

      // Trigger re-authentication
      window.location.reload();
    } catch (error) {
      console.error('Token refresh failed');
      this.clearTokens('refresh-failed');
      window.location.reload();
    }
  }

  clearTokens(reason?: string): void {
    // Clear all token data
    this.accessToken = null;
    this.idToken = null;
    this.tokenExpiryTime = null;

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    // Clear any temporary storage
    try {
      sessionStorage.removeItem('_temp_auth');
    } catch {}

    console.debug(`Tokens cleared: ${reason || 'manual'}`);
  }

  getAccessToken(): string | null {
    // Check if token is still valid before returning
    if (this.tokenExpiryTime && this.tokenExpiryTime < Date.now()) {
      this.clearTokens('expired');
      return null;
    }

    // If token is available, return it immediately
    if (this.accessToken) {
      return this.accessToken;
    }

    // Only retry if the page is hidden (tokens were temporarily cleared for security)
    // If page is visible and no token, it's legitimately unavailable
    if (document.hidden) {
      return this.getAccessTokenWithRetry();
    }

    return null;
  }

  private getAccessTokenWithRetry(): string | null {
    return withExponentialBackoffSync(() => this.accessToken, {
      initialDelay: 50, // Start with 50ms delay
      maxDelay: 200, // Max 200ms delay
      multiplier: 2, // Double the delay each time
      maxRetries: 3, // Try 3 times total
      jitter: false, // No jitter for immediate token restoration
    });
  }

  getIdToken(): IdToken | null {
    // Check if token is still valid before returning
    if (this.tokenExpiryTime && this.tokenExpiryTime < Date.now()) {
      this.clearTokens('expired');
      return null;
    }

    // If token is available, return it immediately
    if (this.idToken) {
      return this.idToken;
    }

    // Only retry if the page is hidden (tokens were temporarily cleared for security)
    // If page is visible and no token, it's legitimately unavailable
    if (document.hidden) {
      return this.getIdTokenWithRetry();
    }

    return null;
  }

  private getIdTokenWithRetry(): IdToken | null {
    return withExponentialBackoffSync(() => this.idToken, {
      initialDelay: 50, // Start with 50ms delay
      maxDelay: 200, // Max 200ms delay
      multiplier: 2, // Double the delay each time
      maxRetries: 3, // Try 3 times total
      jitter: false, // No jitter for immediate token restoration
    });
  }

  getUserInfo(): Auth0UserInfo | null {
    const idToken = this.getIdToken();
    if (!idToken) {
      return null;
    }

    return {
      id: idToken.sub,
      name: idToken.name,
      email: idToken.email,
      logoUrl: this.getUserAvatar(),
      appMetadata: this.getUserAppMetadata(),
    } as Auth0UserInfo;
  }

  getUserAppMetadata(): Record<string, unknown> | null {
    const idToken = this.getIdToken();
    return idToken?.[`https://${AUTH_DOMAIN}/app_metadata`] || null;
  }

  getUserAvatar(): string | null {
    const idToken = this.getIdToken();
    return idToken?.picture || null;
  }

  getUserName(): string | null {
    const idToken = this.getIdToken();
    return idToken?.name || null;
  }

  getUserEmail(): string | null {
    const idToken = this.getIdToken();
    return idToken?.email || null;
  }

  isAuthenticated(): boolean {
    return this.getAccessToken() !== null && this.getIdToken() !== null;
  }

  getTokenExpiryTime(): number | null {
    return this.tokenExpiryTime;
  }

  getTimeUntilExpiry(): number {
    if (!this.tokenExpiryTime) return 0;
    return Math.max(0, this.tokenExpiryTime - Date.now());
  }
}

// Export the enhanced service as TokenService for compatibility
export const TokenService = EnhancedSecureTokenService.getInstance();

// Expose TokenService on window for monitoring.ts to avoid circular dependency
// This allows monitoring.ts to access user ID without importing TokenService
if (typeof window !== 'undefined') {
  interface WindowWithTokenService extends Window {
    __tokenService?: typeof TokenService;
  }
  (window as WindowWithTokenService).__tokenService = TokenService;
}

// Also export the class for testing
export { EnhancedSecureTokenService };
